import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useTranslation } from '../hooks/useTranslation';

const { FiClock, FiTrendingUp, FiShield, FiCheckCircle, FiArrowRight, FiUsers, FiHome, FiCalendar, FiBriefcase, FiHeart } = FiIcons;

function TermVsWholeLife() {
  const { t } = useTranslation();
  
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
  };

  const handleGetQuote = () => {
    window.open('https://quote.burbankinsuranceservices.com/1b0DiB', '_blank');
  };

  const termBenefits = t('termVsWhole.termBenefits');
  const wholeBenefits = t('termVsWhole.wholeBenefits');
  const termProfiles = t('termVsWhole.termProfiles');
  const wholeProfiles = t('termVsWhole.wholeProfiles');
  const termChoiceReasons = t('termVsWhole.termChoiceReasons');
  const wholeChoiceReasons = t('termVsWhole.wholeChoiceReasons');

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerChildren}
            className="text-center mb-16"
          >
            <motion.h1
              variants={fadeInUp}
              className="text-5xl md:text-6xl font-bold text-gray-900 mb-6"
            >
              {t('termVsWhole.heroTitle')}
              <span className="text-blue-600 block text-4xl md:text-5xl mt-2">
                {t('termVsWhole.heroSubtitle')}
              </span>
            </motion.h1>
            
            <motion.p
              variants={fadeInUp}
              className="text-xl text-gray-600 max-w-3xl mx-auto mb-8"
            >
              {t('termVsWhole.heroDescription')}
            </motion.p>
            
            <motion.button
              variants={fadeInUp}
              onClick={handleGetQuote}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center space-x-2 mx-auto"
            >
              <span>{t('termVsWhole.getMyPrice')}</span>
              <SafeIcon icon={FiArrowRight} />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Comparison Infographic */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-4xl font-bold text-gray-900 text-center mb-16"
          >
            {t('termVsWhole.sideByySideComparison')}
          </motion.h2>

          <div className="grid lg:grid-cols-2 gap-8 mb-16">
            {/* Term Life Insurance */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border-2 border-green-200"
            >
              <div className="text-center mb-8">
                <div className="bg-green-600 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiClock} className="text-white text-2xl" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-2">
                  {t('termVsWhole.termLifeInsurance')}
                </h3>
                <p className="text-green-700 font-medium">
                  {t('termVsWhole.termSubtitle')}
                </p>
              </div>

              <div className="space-y-4 mb-8">
                {termBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <SafeIcon icon={FiCheckCircle} className="text-green-600 text-xl" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>

              <div className="bg-white rounded-lg p-6 mb-6">
                <h4 className="font-bold text-gray-900 mb-3">{t('termVsWhole.typicalCosts')}</h4>
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex justify-between">
                    <span>30-year-old, $500K, 20-year term:</span>
                    <span className="font-semibold text-green-600">~$25/month</span>
                  </div>
                  <div className="flex justify-between">
                    <span>40-year-old, $500K, 20-year term:</span>
                    <span className="font-semibold text-green-600">~$45/month</span>
                  </div>
                </div>
              </div>

              <button
                onClick={handleGetQuote}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-semibold transition-colors"
              >
                {t('termVsWhole.getTermQuote')}
              </button>
            </motion.div>

            {/* Whole Life Insurance */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border-2 border-blue-200"
            >
              <div className="text-center mb-8">
                <div className="bg-blue-600 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiTrendingUp} className="text-white text-2xl" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-2">
                  {t('termVsWhole.wholeLifeInsurance')}
                </h3>
                <p className="text-blue-700 font-medium">
                  {t('termVsWhole.wholeSubtitle')}
                </p>
              </div>

              <div className="space-y-4 mb-8">
                {wholeBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <SafeIcon icon={FiCheckCircle} className="text-blue-600 text-xl" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>

              <div className="bg-white rounded-lg p-6 mb-6">
                <h4 className="font-bold text-gray-900 mb-3">{t('termVsWhole.typicalCosts')}</h4>
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex justify-between">
                    <span>30-year-old, $500K whole life:</span>
                    <span className="font-semibold text-blue-600">~$400/month</span>
                  </div>
                  <div className="flex justify-between">
                    <span>40-year-old, $500K whole life:</span>
                    <span className="font-semibold text-blue-600">~$650/month</span>
                  </div>
                </div>
              </div>

              <button
                onClick={handleGetQuote}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold transition-colors"
              >
                {t('termVsWhole.getWholeQuote')}
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who Should Choose What */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-4xl font-bold text-gray-900 text-center mb-16"
          >
            {t('termVsWhole.whoShouldChooseWhat')}
          </motion.h2>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Term Life Profiles */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerChildren}
            >
              <h3 className="text-2xl font-bold text-green-600 mb-8 text-center">
                {t('termVsWhole.termPerfectFor')}
              </h3>
              <div className="space-y-6">
                {termProfiles.map((profile, index) => (
                  <motion.div
                    key={index}
                    variants={fadeInUp}
                    className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-green-500"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="bg-green-100 rounded-full p-3">
                        <SafeIcon icon={index === 0 ? FiUsers : index === 1 ? FiBriefcase : FiHome} className="text-green-600 text-xl" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-xl font-bold text-gray-900 mb-2">{profile.title}</h4>
                        <p className="text-gray-600 mb-3">{profile.description}</p>
                        <div className="flex flex-col sm:flex-row sm:justify-between text-sm">
                          <span className="text-green-600 font-medium">{profile.age}</span>
                          <span className="text-gray-500 italic">{profile.scenario}</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Whole Life Profiles */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerChildren}
            >
              <h3 className="text-2xl font-bold text-blue-600 mb-8 text-center">
                {t('termVsWhole.wholePerfectFor')}
              </h3>
              <div className="space-y-6">
                {wholeProfiles.map((profile, index) => (
                  <motion.div
                    key={index}
                    variants={fadeInUp}
                    className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-blue-500"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="bg-blue-100 rounded-full p-3">
                        <SafeIcon icon={index === 0 ? FiTrendingUp : index === 1 ? FiHeart : FiShield} className="text-blue-600 text-xl" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-xl font-bold text-gray-900 mb-2">{profile.title}</h4>
                        <p className="text-gray-600 mb-3">{profile.description}</p>
                        <div className="flex flex-col sm:flex-row sm:justify-between text-sm">
                          <span className="text-blue-600 font-medium">{profile.age}</span>
                          <span className="text-gray-500 italic">{profile.scenario}</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Decision Helper */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              {t('termVsWhole.quickDecisionHelper')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('termVsWhole.answerQuestions')}
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
            className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8"
          >
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {t('termVsWhole.chooseTermIf')}
                </h3>
                <div className="space-y-3">
                  {termChoiceReasons.map((reason, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <SafeIcon icon={FiCheckCircle} className="text-green-500 text-lg" />
                      <span className="text-gray-700">{reason}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {t('termVsWhole.chooseWholeIf')}
                </h3>
                <div className="space-y-3">
                  {wholeChoiceReasons.map((reason, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <SafeIcon icon={FiCheckCircle} className="text-blue-500 text-lg" />
                      <span className="text-gray-700">{reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            <motion.h2
              variants={fadeInUp}
              className="text-4xl font-bold text-white mb-6"
            >
              {t('termVsWhole.readyToDecide')}
            </motion.h2>
            
            <motion.p
              variants={fadeInUp}
              className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto"
            >
              {t('termVsWhole.noAnalysisParalysis')}
            </motion.p>
            
            <motion.div
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <button
                onClick={handleGetQuote}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center space-x-2"
              >
                <span>{t('termVsWhole.getPersonalizedQuote')}</span>
                <SafeIcon icon={FiArrowRight} />
              </button>
            </motion.div>
            
            <motion.p
              variants={fadeInUp}
              className="text-gray-400 mt-4"
            >
              {t('termVsWhole.compareBothOptions')}
            </motion.p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default TermVsWholeLife;